/**
 * Shared types for the Sai Clicker game core.
 *
 * Reference: `docs/09_progression_math.md` (sections I–VII) and
 * `docs/02_game_design.md` (mechanics A–G, resources).
 *
 * Convention: numeric resources are integers stored as `number`. We intentionally
 * stay within JS safe-integer range (≤ 2^53−1) up to mid-epoch 11; beyond that
 * we switch to BigInt-backed strings — see `src/conversion.ts` for the migration
 * marker. For the alpha (epochs 1–3) `number` is more than enough.
 */

// ---------------------------------------------------------------------------
// Epochs and resources
// ---------------------------------------------------------------------------

/** 1-indexed epoch (1 = Spark, 12 = Creator). */
export type Epoch = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export const ALL_EPOCHS: readonly Epoch[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

/** Every distinct resource the game tracks. See `docs/09` §I.1. */
export type ResourceId =
  | 'awarenesses' // mech A
  | 'spirit_balls' // conversion of awarenesses
  | 'body_impulses' // mech B
  | 'meaning_crystals' // mech C
  | 'sprouts' // mech D
  | 'frequencies' // mech E
  | 'unlock_keys' // mech F
  | 'seeds_of_light' // prestige
  | 'light_of_origin'; // ascension

export type ResourceState = Readonly<Record<ResourceId, number>>;

// ---------------------------------------------------------------------------
// Floating phrases (Atlas)
// ---------------------------------------------------------------------------

/** Five base layers from `docs/02` plus two deeper layers from `docs/09` §III. */
export type PhraseLayer =
  | 'numeric'
  | 'sensory'
  | 'body'
  | 'elemental'
  | 'awareness'
  | 'program'
  | 'creator';

/**
 * One entry in the floating-phrase pool. Weighted random pick.
 *
 * `special` marks phrases that appear only on milestones (e.g. every 10th tap)
 * rather than via normal weighted draw.
 */
export interface PhraseEntry {
  readonly id: string;
  readonly text: string;
  readonly layer: PhraseLayer;
  readonly epoch: Epoch;
  /** Relative weight in the weighted pool. Ignored if `special` is set. */
  readonly weight: number;
  readonly special?: 'on10thTap' | 'on100thTap' | 'on1000thTap';
}

// ---------------------------------------------------------------------------
// Lessons (Book of Lessons) — achievements with multiplier bonuses
// ---------------------------------------------------------------------------

/**
 * A lesson condition is evaluated against the current game state. We model it
 * as a discriminated union so the type system enforces exhaustiveness when
 * new condition kinds are added.
 */
export type LessonCondition =
  | { readonly kind: 'firstTap' }
  | { readonly kind: 'awarenessesAtLeast'; readonly value: number }
  | { readonly kind: 'spiritBallsAtLeast'; readonly value: number }
  | { readonly kind: 'tapsInRhythm'; readonly value: number }
  | { readonly kind: 'crystallizationsAtLeast'; readonly value: number }
  | { readonly kind: 'returnAfterHours'; readonly value: number }
  | { readonly kind: 'daysStreak'; readonly value: number }
  | { readonly kind: 'tapAtHour'; readonly fromHour: number; readonly toHour: number }
  | { readonly kind: 'collectPhrasesInEpoch'; readonly count: number; readonly epoch: Epoch }
  | { readonly kind: 'collectAllPhrasesInEpoch'; readonly epoch: Epoch }
  | { readonly kind: 'collectAllPhrasesInLayer'; readonly layer: PhraseLayer }
  | { readonly kind: 'reachEpoch'; readonly epoch: Epoch }
  | { readonly kind: 'silenceSecondsAtLeast'; readonly value: number }
  | { readonly kind: 'profileNameSet' }
  | { readonly kind: 'goldenSparkCaught'; readonly count: number };

export interface LessonDefinition {
  readonly id: string;
  /** Short Russian title in `language of presence`. */
  readonly title: string;
  readonly epoch: Epoch;
  readonly condition: LessonCondition;
  /** Bonus added to `lessonsBonus` global multiplier component. `0.01` = +1 %. */
  readonly bonus: number;
  readonly hidden?: boolean;
}

// ---------------------------------------------------------------------------
// Sai activations — context-driven full-screen pauses
// ---------------------------------------------------------------------------

export type ActivationTrigger =
  | { readonly kind: 'firstTap' }
  | { readonly kind: 'tapCount'; readonly value: number }
  | { readonly kind: 'firstCrystallization' }
  | { readonly kind: 'enterEpoch'; readonly epoch: Epoch }
  | { readonly kind: 'firstBreathCycle' }
  | { readonly kind: 'returnAfterHours'; readonly value: number }
  | { readonly kind: 'firstGoldenSpark' }
  | { readonly kind: 'lessonUnlocked'; readonly lessonId: string };

export interface SaiActivation {
  readonly id: string;
  readonly trigger: ActivationTrigger;
  readonly text: string;
  /** Optional preface line shown above the main text in smaller type. */
  readonly preface?: string;
}

// ---------------------------------------------------------------------------
// Whispers of the Grove — running ticker
// ---------------------------------------------------------------------------

export type WhisperCategory = 'sai' | 'grove' | 'community' | 'context';

export interface WhisperEntry {
  readonly id: string;
  readonly text: string;
  readonly category: WhisperCategory;
  /** If set, the whisper is only shown when this condition holds. */
  readonly condition?: LessonCondition;
}

// ---------------------------------------------------------------------------
// Game state — the authoritative shape held by the server (and mirrored client-side)
// ---------------------------------------------------------------------------

export interface GameStats {
  readonly totalTaps: number;
  readonly totalTapsRejected: number; // cooldown-blocked attempts
  readonly totalCrystallizations: number;
  readonly totalGoldenSparksCaught: number;
  /** Best rhythm streak ever achieved (used by `tapsInRhythm` lessons). */
  readonly tapsInRhythmBest: number;
  /** Currently active rhythm streak — resets when a tap arrives late. */
  readonly tapsInRhythmCurrent: number;
  readonly lastTapAt: number | null; // epoch ms; null = no taps yet
  readonly firstTapAt: number | null;
  readonly lastActiveAt: number; // epoch ms — used for offline progress
  readonly daysStreak: number;
}

export interface GameState {
  readonly epoch: Epoch;
  readonly resources: ResourceState;
  readonly stats: GameStats;

  /**
   * Tap cooldown in ms — minimum gap between accepted taps. Lowered by
   * upgrades (eventually down to MIN_TAP_COOLDOWN_MS). Set to
   * BASE_TAP_COOLDOWN_MS on a fresh game.
   */
  readonly cooldownMs: number;

  /**
   * Atlas — phrase id → how many times it has appeared in a tap.
   * `.has(id)` still answers «is it collected?» (true after first sighting).
   * The count enables: sort-by-frequency in Atlas UI, anti-repeat weighting
   * in phrase selection, «появлялась N раз» badges. Doc 28 §3.
   */
  readonly atlasCollected: ReadonlyMap<string, number>;
  /** Lesson IDs the player has unlocked. */
  readonly lessonsUnlocked: ReadonlySet<string>;

  /** Profile fields */
  readonly profile: {
    readonly displayName: string | null;
  };

  /** Tunable boosts that originate from external systems (prestige, ascension, …). */
  readonly globalBoosts: {
    readonly prestigeMultiplier: number; // from Seeds of Light
    readonly silentBlessingMultiplier: number; // from a quiet week
  };

  /**
   * Купленные апгрейды как `upgradeId → currentLevel` (level 0 = не куплен,
   * но обычно такие записи отсутствуют). Сериализуется в localStorage
   * как `[string, number][]` через helpers в `upgrades.ts`.
   * См. `docs/14_upgrades_system.md`.
   */
  readonly purchasedUpgrades: ReadonlyMap<string, number>;
}

// ---------------------------------------------------------------------------
// Tap result (returned by `applyTap`)
// ---------------------------------------------------------------------------

/**
 * Result of an attempted tap.
 *
 * When `accepted: false` the tap was rejected because the cooldown
 * hadn't completed — `state` is unchanged, `awarenessesGained === 0`,
 * `phrase === null`. UI should not animate a real ding; a brief
 * "not ready" hint is fine.
 *
 * When `accepted: true`, behavior matches the pre-cooldown contract:
 * state advances, awareness rises, a phrase is selected.
 */
export interface TapResult {
  readonly accepted: boolean;
  readonly awarenessesGained: number;
  readonly phrase: PhraseEntry | null;
  readonly state: GameState;
  /** Lessons that just unlocked because of this tap (if any). */
  readonly newlyUnlockedLessons: readonly LessonDefinition[];
  /** Sai activation that should fire because of this tap (if any). */
  readonly triggeredActivation: SaiActivation | null;
  /**
   * Whether this tap landed within the rhythm window (= cooldown end
   * + RHYTHM_WINDOW_MS). True extends streak, false resets it to 1.
   * Always false when `accepted: false`.
   */
  readonly wasInRhythm: boolean;
}

// ---------------------------------------------------------------------------
// Crystallization result
// ---------------------------------------------------------------------------

export interface CrystallizationPreview {
  readonly awarenessesToSpend: number;
  readonly spiritBallsToGain: number;
}

export interface CrystallizationResult {
  readonly spiritBallsGained: number;
  readonly state: GameState;
  readonly newlyUnlockedLessons: readonly LessonDefinition[];
  readonly triggeredActivation: SaiActivation | null;
  readonly didAdvanceEpoch: boolean;
  readonly newEpoch: Epoch;
}

// ---------------------------------------------------------------------------
// Multiplier breakdown — for UI transparency
// ---------------------------------------------------------------------------

export interface MultiplierBreakdown {
  readonly lessonsBonus: number; // 0.01 = +1 %
  readonly atlasBonus: number;
  readonly upgradesBonus: number; // sum across all purchased tap-multiplier upgrades
  readonly guidesBonus: number;
  readonly artifactSetsBonus: number;
  readonly roleBonus: number;
  readonly mantraBonus: number;
  readonly seasonBonus: number;
  readonly silentBlessing: number; // straight multiplier (e.g. 1.1)
  readonly prestige: number; // straight multiplier from Seeds of Light
  readonly total: number; // final composed multiplier
}
